import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ShowCard from '@/components/base/ShowCard';
import { categories, getShowsByCategory, shows } from '@/mocks/shows';

export default function CategoryPage() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';

  const currentCategory = useMemo(() => {
    return categories.find(c => c.slug === categorySlug) || categories[0];
  }, [categorySlug]);

  const filteredShows = useMemo(() => {
    let result = getShowsByCategory(categorySlug || 'all');
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = shows.filter(s =>
        s.title.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.tags.some(t => t.toLowerCase().includes(q)) ||
        s.category.toLowerCase().includes(q)
      );
    }
    return result;
  }, [categorySlug, searchQuery]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as unknown as ScrollBehavior });
  }, [categorySlug, searchQuery]);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${currentCategory.name} - 6789影院综艺节目分类`,
      description: `6789影院${currentCategory.name}综艺节目大全，免费在线观看高清流畅不缓冲。每日更新热门${currentCategory.name}节目。`,
      url: `https://www.6789cinema.com/category/${categorySlug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '6789影院',
        url: 'https://www.6789cinema.com/',
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.6789cinema.com/' },
          { '@type': 'ListItem', position: 2, name: currentCategory.name },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [currentCategory, categorySlug]);

  return (
    <>
      <Navbar />
      <main>
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: currentCategory.name },
        ]} />

        <section className="px-4 md:px-6 pb-14 md:pb-16">
          <div className="max-w-[1400px] mx-auto">
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {searchQuery ? `搜索"${searchQuery}"的结果` : currentCategory.name}
              </h1>
              <p className="mt-2 text-sm text-foreground-500">
                {searchQuery
                  ? `找到 ${filteredShows.length} 个相关综艺节目`
                  : `6789影院${currentCategory.name}综艺节目大全，免费在线观看高清流畅不缓冲`
                }
              </p>
            </div>

            {!searchQuery && (
              <div className="flex items-center gap-3 mb-8 overflow-x-auto scrollbar-hide pb-2">
                {categories.map(cat => (
                  <Link
                    key={cat.slug}
                    to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                    className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      cat.slug === categorySlug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}

            {filteredShows.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5" data-product-shop>
                {filteredShows.map(show => (
                  <ShowCard key={show.id} show={show} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 md:w-20 md:h-20 mx-auto flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-search-line text-2xl md:text-3xl text-foreground-400"></i>
                </div>
                <h3 className="mt-4 text-lg font-heading font-semibold text-foreground-700">未找到相关节目</h3>
                <p className="mt-1 text-sm text-foreground-500">试试其他关键词或浏览全部综艺节目</p>
                <Link
                  to="/"
                  className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}